/**
 * Google Reviews Service
 * Handles fetching and managing Google Reviews from Supabase
 */

import { supabase } from '../lib/supabase';

class GoogleReviewsService {
  /**
   * Fetch all visible reviews with category information
   * @returns {Promise<Array>} Array of reviews with category data
   */
  async getAllReviews() {
    try {
      const { data, error } = await supabase
        .from('google_reviews')
        .select(`
          *,
          treatment_category:treatment_categories(
            id,
            name,
            slug
          )
        `)
        .eq('is_visible', true)
        .order('review_time', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllReviews:', error);
      return [];
    }
  }

  /**
   * Fetch reviews by category
   * @param {string} categorySlug - Category slug to filter by
   * @returns {Promise<Array>} Array of reviews for the category
   */
  async getReviewsByCategory(categorySlug) {
    try {
      if (categorySlug === 'all') {
        return this.getAllReviews();
      }

      const { data, error } = await supabase
        .from('google_reviews')
        .select(`
          *,
          treatment_category:treatment_categories!inner(
            id,
            name,
            slug
          )
        `)
        .eq('is_visible', true)
        .eq('treatment_categories.slug', categorySlug)
        .order('review_time', { ascending: false });

      if (error) {
        console.error('Error fetching reviews by category:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getReviewsByCategory:', error);
      return [];
    }
  }

  /**
   * Fetch featured reviews
   * @param {number} limit - Maximum number of reviews to fetch
   * @returns {Promise<Array>} Array of featured reviews
   */
  async getFeaturedReviews(limit = 6) {
    try {
      const { data, error } = await supabase
        .from('google_reviews')
        .select(`
          *,
          treatment_category:treatment_categories(
            id,
            name,
            slug
          )
        `)
        .eq('is_visible', true)
        .eq('is_featured', true)
        .order('review_time', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching featured reviews:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getFeaturedReviews:', error);
      return [];
    }
  }

  /**
   * Get review statistics
   * @returns {Promise<Object>} Statistics about reviews
   */
  async getReviewStats() {
    try {
      const { data, error } = await supabase
        .from('google_reviews')
        .select('rating, treatment_category_id')
        .eq('is_visible', true);

      if (error) {
        console.error('Error fetching review stats:', error);
        return {
          total: 0,
          averageRating: 0,
          byCategory: {},
        };
      }

      const total = data?.length || 0;
      const averageRating = total > 0
        ? (data.reduce((sum, review) => sum + review.rating, 0) / total).toFixed(1)
        : 0;

      // Count by category
      const byCategory = {};
      data?.forEach(review => {
        const categoryId = review.treatment_category_id || 'uncategorized';
        byCategory[categoryId] = (byCategory[categoryId] || 0) + 1;
      });

      return {
        total,
        averageRating: parseFloat(averageRating),
        byCategory,
      };
    } catch (error) {
      console.error('Error in getReviewStats:', error);
      return {
        total: 0,
        averageRating: 0,
        byCategory: {},
      };
    }
  }

  /**
   * Trigger manual sync of Google Reviews
   * Calls the Edge Function to sync reviews
   * @returns {Promise<Object>} Sync result
   */
  async syncReviews() {
    try {
      const { data, error } = await supabase.functions.invoke('sync-google-reviews');

      if (error) {
        console.error('Error syncing reviews:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in syncReviews:', error);
      throw error;
    }
  }
}

export default new GoogleReviewsService();

